import React from 'react';
import { NavItem, NavDropdownContentProps } from '../NavHeader.type';
import { isPopulatedNavDropdown, isPopulatedNavItem } from './utils';
import { NavLinkItem } from '../navItems/NavLink/NavLinkItem';
import { NavDropdownItem } from '../navItems/NavDropdown/NavDropdownItem';
import cn from 'classnames';
import { PopoverMenu, PopoverMenuProps } from '@/ui-components/molecules/PopoverMenu/PopoverMenu';
import styles from '../navheader.module.scss';

interface Props {
    handleSearchClose: () => void;
    index: number;
    isDarkTheme: boolean;
    item: NavItem | NavDropdownContentProps | PopoverMenuProps;
    navDropdownHoveredIndex: number | null;
    setHelpCenterHovered: (isHovered: boolean) => void;
    setNavDropdownHoveredIndex: (index: number | null) => void;
    searchOpen: boolean;
    scheduleHoverClose: () => void;
    cancelHoverClose: () => void;
    popoverHoveredIndex: number | null;
    setPopoverHoveredIndex: (index: number | null) => void;
}

function isPopoverMenuProps(item: NavItem | NavDropdownContentProps | PopoverMenuProps): item is PopoverMenuProps {
    return (
        typeof (item as PopoverMenuProps)?.mainHeading !== 'undefined' ||
        typeof (item as PopoverMenuProps)?.linkSet !== 'undefined'
    );
}

export function DesktopNavItems({
    handleSearchClose,
    index,
    isDarkTheme,
    item,
    navDropdownHoveredIndex,
    setHelpCenterHovered,
    setNavDropdownHoveredIndex,
    searchOpen,
    scheduleHoverClose,
    cancelHoverClose,
    popoverHoveredIndex,
    setPopoverHoveredIndex,
}: Props): React.ReactNode {
    if (isPopulatedNavItem(item)) {
        return (
            <NavLinkItem
                index={index}
                isDarkTheme={isDarkTheme}
                item={item}
                setHelpCenterHovered={setHelpCenterHovered}
                setNavDropdownHoveredIndex={setNavDropdownHoveredIndex}
            />
        );
    } else if (isPopulatedNavDropdown(item)) {
        return (
            <NavDropdownItem
                handleSearchClose={handleSearchClose}
                index={index}
                isDarkTheme={isDarkTheme}
                navDropdownContent={item}
                navDropdownHoveredIndex={navDropdownHoveredIndex}
                setHelpCenterHovered={setHelpCenterHovered}
                setNavDropdownHoveredIndex={setNavDropdownHoveredIndex}
                searchOpen={searchOpen}
                scheduleHoverClose={scheduleHoverClose}
                cancelHoverClose={cancelHoverClose}
            />
        );
    } else if (isPopoverMenuProps(item)) {
        const isOpen = popoverHoveredIndex === index;

        const handleClick = () => {
            if (searchOpen) {
                setPopoverHoveredIndex(isOpen ? null : index);
                handleSearchClose?.();
                setHelpCenterHovered(false);
            }
        };

        return (
            <li
                key={index}
                onMouseEnter={() => {
                    if (!searchOpen) {
                        cancelHoverClose();
                        setPopoverHoveredIndex(index);
                        setHelpCenterHovered(false);
                    }
                }}
                onMouseLeave={() => {
                    if (!searchOpen) {
                        scheduleHoverClose();
                    }
                }}
            >
                <button
                    className={styles.supportButton}
                    onClick={handleClick}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            setHelpCenterHovered(false);
                            setPopoverHoveredIndex(index);
                        } else if (e.key === 'Escape') {
                            setPopoverHoveredIndex(index);
                            setHelpCenterHovered(false);
                        }
                    }}
                    aria-expanded={isOpen}
                    aria-label={item.mainHeading}
                >
                    <span className={cn(styles.helpCenter, { [styles.darktheme]: isDarkTheme })}>
                        {item.mainHeading}
                    </span>
                    <PopoverMenu {...item} className={styles.supportPopover} open={isOpen} />
                </button>
            </li>
        );
    }

    return null;
}
